import { useContext, useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Container, Button } from "react-bootstrap";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { ExaminationHistory, ExaminationGrade, ExamineeStatus, ExamineeStatusReason } from '../Dashboard/Exams/Models/ExaminationHistory';
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";
import { Link } from "react-router-dom";


export default function ExamHistory() {
    const validInitialSpecialtyCertYear = 2017;
    const validForAnyExamAdministeredYear = 2021;
    const [examHistory, setExamHistory] = useState<Array<ExaminationHistory>>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [examinationIdDownloading, setExaminationIdDownloading] = useState<number>(null);

    useEffect(() => {
        const getExams = () => {
            abpnClient.get<Array<ExaminationHistory>>(`/api/Examination/GetExaminationHistory`, authContext)
                .then(exams => {
                    setExamHistory(exams);
                    setIsLoading(false);
                });
        }
        getExams();
    }, []);

    const authContext = useContext(AuthContext);

    const getGrade = (exam: ExaminationHistory) => {
        if (exam.examineeStatusReasonId === ExamineeStatusReason.Invalidated) return exam.examineeStatusReason;

        if (exam.examinationGrade === ExaminationGrade.Pass) return "Pass";

        if (exam.examinationGrade === ExaminationGrade.Fail) return "Fail";

        if (exam.examineeStatus === ExamineeStatus.UnexcusedAbsence) return "Unexcused Absence";

        if (exam.examinationGrade === null) return "N/A"

        return "";
    }

    const getCandidateStatusDescription = (exam: ExaminationHistory) => {
        return exam.candidateStatusReason;
    }

    const downloadExamResultsAvailable = (exam: ExaminationHistory) => {
        if (exam.examineeStatusReasonId === ExamineeStatusReason.Invalidated || !exam.hasExamineeDocuments) return false;

        var examStartDate = getDateWithOffset(exam.startDate);

        if (exam.isPrimary && !exam.isMoc && examStartDate.getFullYear() >= validInitialSpecialtyCertYear) return true;

        return examStartDate.getFullYear() >= validForAnyExamAdministeredYear;
    }

    const showConfirmPersonalInfoMessage = (exam: ExaminationHistory) => {
        var result = new Date();
        result.setDate(result.getDate() - 120);

        var cutoff = getDateWithOffset(result.toString());
        var endDateCheck = getDateWithOffset(exam.endDate);

        var isInConfirmDateRange = endDateCheck >= cutoff;
        var isMoc = exam.isMoc;
        return exam.examinationGrade === ExaminationGrade.Pass && isInConfirmDateRange && !isMoc;
    }

    const showConfirmPersonalInfoMessageMOC = (exam: ExaminationHistory) => {
        var result = new Date();
        result.setDate(result.getDate() - 120);

        var cutoff = getDateWithOffset(result.toString());
        var endDateCheck = getDateWithOffset(exam.endDate);

        var isInConfirmDateRange = endDateCheck >= cutoff;
        var isMoc = exam.isMoc;
        return exam.examinationGrade === ExaminationGrade.Pass && isInConfirmDateRange && isMoc;
    }

    const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>, examinationId: number) => {
        e.preventDefault();
        e.stopPropagation();

        setExaminationIdDownloading(examinationId);
        setIsDownloading(true);

        var response = await abpnClient.getResponse(`/api/examination/DownloadScoreReport?examinationId=${examinationId}`, authContext)
        var blob = await response.blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `ExaminationGrade.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);

        setIsDownloading(false);
    }

    if (isLoading) return <AbpnSpinner></AbpnSpinner>;

    if (examHistory == null || examHistory.length === 0) return <div><h4>No Examination History</h4></div>
    
    return (
        <Container>
            <div >
                <h5 className="font-weight-bolder mb-3">Examination History</h5>
            </div>
            <Card className="mb-4" border="pp-secondary-grey">
                <Card.Body>
                {examHistory.map(exam => {
                    return (
                        <Fragment>
                            <Row noGutters className="mt-3" key={exam.examinationId}>
                                <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{exam.certificationName.toUpperCase()}</div>
                                    <div className="mb-1 pp-font-size-smaller">{exam.moduleSelection}{exam.formattedCnpType}</div>
                                </Col>
                                <Col xs={12} lg={10} xl={10}>
                                    <Row>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Exam Start Date</div>
                                            <div className="mb-1 pp-font-size-smaller">{getDateWithOffset(exam.startDate).toLocaleDateString()}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Examination Name</div>
                                            <div className="mb-1 pp-font-size-smaller">{exam.title}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Grade</div>
                                            <div className="mb-1 pp-font-size-smaller">{getGrade(exam)}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={2} xl={2}>
                                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Application Status</div>
                                            <div className="mb-1 pp-font-size-smaller">{getCandidateStatusDescription(exam)}</div>
                                        </Col>
                                        <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                            {downloadExamResultsAvailable(exam) ?
                                            (isDownloading && examinationIdDownloading === exam.examinationId) ? <AbpnSpinner /> :
                                            <Button variant="outline-pp-primary" onClick={(e) => download(e, exam.examinationId)}>Download Exam Results</Button> : null
                                            }
                                        </Col>
                                    </Row>
                                    {showConfirmPersonalInfoMessage(exam) &&
                                    <Fragment>
                                        <hr />
                                        <Row>
                                            <Col className="mb-1" xs={12} md={12} lg={8} xl={8}>
                                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Confirm your Personal Information within 30 days of the date of the Grade Letter found in Exam Results.
                                                </div>
                                                <div className="mb-1 pp-font-size-smaller">
                                                    Confirm your Name and Title along with your Contact, Shipping, and Public addresses to ensure successful
                                                    delivery of your certificate and correct publication of your name and address.
                                                </div>
                                            </Col>
                                            <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                                <Link to="/AccountInformation">
                                                    <Button variant="pp-primary">Confirm Personal Information</Button>
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                    }
                                    {showConfirmPersonalInfoMessageMOC(exam) &&
                                    <Fragment>
                                        <hr />
                                        <Row>
                                            <Col className="mb-1" xs={12} md={12} lg={8} xl={8}>
                                                <div className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Confirm your Personal Information within 30 days of the date of the Grade Letter found in Exam Results.
                                                </div>
                                                <div className="mb-1 pp-font-size-smaller">
                                                    It is your responsibility to keep the information current in your ABPN Physician Portal throughout your career.
                                                </div>
                                            </Col>
                                            <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                                                <Link to="/AccountInformation">
                                                    <Button variant="pp-primary">Confirm Personal Information</Button>
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                    }
                                </Col>
                            </Row>
                            <hr />
                        </Fragment>
                    )
                })}
                </Card.Body>
            </Card>
        </Container>
    );
}